import { Buffer } from 'buffer/'
import crypto from 'crypto'
import { getAddressSpecificBody } from '@helpers/checkout/shipping-section'
import { setOrder, setCheckoutStep } from '@redux/modules/checkout'
import { updateAddress, updatePayment } from '@services/checkout'
import { getOrder } from '../global'
import { getRemainingTotal } from './payment-section'

export const getOrderTotal = total => {
  const order = getOrder()
  let apTotal = order.total
  if (order && order.orderId && (total > 0 || order.total > 0)) {
    const applyFinance =
      order &&
      order.paymentInfo &&
      order.paymentInfo.filter(
        payment =>
          payment.paymentType === 'FIN' ||
          (payment.paymentType === 'DBUY' && payment.paymentProperties && payment.paymentProperties.hasPayments),
      ).length > 0
    apTotal = Math.floor((getRemainingTotal(true, null, true, applyFinance).total || total) * 100) / 100
  }
  return apTotal
}

/**
 * Scrolls to the element with id = elementId after 1 second
 * @param {string} elementId
 */
export const scrollTo = elementId => {
  setTimeout(() => {
    if (document.getElementById(elementId)) {
      document.getElementById(elementId).scrollIntoView({
        behavior: 'smooth',
      })
    }
  }, 1000)
}

export const decryptFinancePlans = (uvfinancePlan, encryptedFinancePlans) => {
  if (!uvfinancePlan || !encryptedFinancePlans) return ''
  const split = uvfinancePlan.split('|')
  let synchronyCode = split[0]
  if (encryptedFinancePlans) {
    const decipher = crypto.createDecipheriv(
      process.env.GATSBY_FINANCE_CODE_ENCRYPTION_ALGORITHM,
      Buffer.from(process.env.GATSBY_FINANCE_CODE_ENCRYPTION_KEY),
      process.env.GATSBY_FINANCE_CODE_ENCRYPTION_IV,
    )
    const encryptedCodes = Buffer.from(encryptedFinancePlans, process.env.GATSBY_FINANCE_CODE_ENCRYPTION_RADIX)
    let decryptedCodes = Buffer.concat([decipher.update(encryptedCodes), decipher.final()])
    decryptedCodes = decryptedCodes.toString().split('|')
    synchronyCode = decryptedCodes.length > 1 ? decryptedCodes[1] : decryptedCodes[0]
  }
  return synchronyCode
}

export const formatCurrency = (price, forceCents = false) => {
  let priceNumber = Number(price)
  if (forceCents || !Number.isInteger(priceNumber)) {
    priceNumber = priceNumber.toFixed(2)
  } else {
    priceNumber = priceNumber.toFixed(0)
  }
  return priceNumber
}

export const formatNumber = val => Number(val.replace(',', ''))

export const getBillingAddress = ({ Address1 = '', Address2 = '', City = '', State = '', ZipCode = '' }) => {
  if (!Address1) {
    return {}
  }
  return {
    address1: Address1,
    address2: Address2 === 'null' || Address2 === 'undefined' ? '' : Address2,
    city: City,
    country_code: 'US',
    state: State,
    zip: ZipCode?.slice(0, 5),
  }
}

export const getTotal = (requiresDownPayment, order) => {
  let total = 0
  if (requiresDownPayment) {
    if (order?.paymentInfo?.length && order?.amountDue > 0) {
      total = order.amountDue - order.tax - order.totalDeliveryCharge
    } else {
      total = order.subtotal
    }
  } else if (order?.paymentInfo?.length && order?.amountDue > 0) {
    total = order.amountDue
  } else {
    total = order.total
  }
  return formatCurrency(total, true)
}

export const updateOrderPayment = async (dispatch, paymentAmount, paymentProperties, order) => {
  const { firstName, lastName, email, phone } = order?.contact ?? {}
  const { address1, address2, city, state, zip } = order?.shippingAddress ?? {}
  const newOrder = {
    ...order,
    billingAddress: { address1, address2, city, state, zip },
    payer: { ...order.payer, email, firstName, lastName, phone },
    selectedPaymentType: 'Rooms To Go',
  }

  const dbPaymentInfo = [
    ...(order?.paymentInfo?.filter(payment => payment.paymentType === 'GIFT') ?? []),
    {
      authorized: false,
      authorizedAmount: formatNumber(paymentAmount),
      paymentProperties,
      paymentType: 'DBUY',
    },
  ]

  let updateSucceeded = false
  let addrOrder

  try {
    addrOrder = await updateAddress(getAddressSpecificBody(newOrder, true))
  } catch (error) {
    console.error('Address update error >>> ', error?.message)
  }

  if (!addrOrder) {
    return updateSucceeded
  }

  dispatch(setOrder(addrOrder))
  let paidOrder
  try {
    paidOrder = await updatePayment({ orderId: order.orderId, paymentInfo: dbPaymentInfo })
  } catch (error) {
    console.error('Payment update error >>> ', error?.message)
  }

  if (!paidOrder) {
    return updateSucceeded
  }

  updateSucceeded = true

  if (paidOrder.amountDue <= 0) {
    dispatch(setOrder({ ...paidOrder, isNonFinanceCredit: true }))
  } else {
    dispatch(setOrder({ ...paidOrder, selectedPaymentType: 'Credit', isNonFinanceCredit: true }))
  }
  return updateSucceeded
}

export const removeDbuyPayment = async (dispatch, order, sendAlert) => {
  let newOrder
  const giftCardPayment = order?.paymentInfo?.filter(payment => payment.paymentType === 'GIFT') ?? []

  try {
    newOrder = await updatePayment({ orderId: order.orderId, paymentInfo: giftCardPayment })
  } catch (error) {
    console.error('Payment update error >>> ', error?.message)
  }

  dispatch(setOrder({ ...newOrder, selectedPaymentType: 'Rooms To Go' }))
  dispatch(setCheckoutStep('payment'))
  sendAlert('Your payment session has expired, please try again.') // TODO - this doesn't work since component is unmounted
}

export const getDbuyStatus = (clientTokenStatus, cliRejected) => {
  const { Address1, responseCode, StatusCode: statusCode } = clientTokenStatus ?? {}

  switch (statusCode) {
    case '002':
    case '003': {
      // statusCode of '003' with no Address1 property indicates CVV is incorrect
      return Address1 ? 'Account Verified' : 'Invalid CVV'
    }
    case '000':
    case '033': {
      return 'Payment Approved'
    }
    case '001':
    case '100': {
      return cliRejected ? 'CLI Refused' : 'Customer Terminated'
    }
    case '401': {
      return 'Token Expired'
    }
    case '403': {
      return 'Address Verification Failed'
    }
    case '00': {
      return 'eApply Approved'
    }
    case '07': {
      return 'eApply Declined'
    }
    default: {
      if (responseCode === '503' || responseCode === '500') {
        return 'Service Unavailable'
      }
      if (responseCode === '404') {
        return 'Token Not Found'
      }
      return ''
    }
  }
}
